<template>
  <div ref="popupRef"
    class="dropdown-menu z-[1030] dropdown-menu-right box-border flex flex-col border p-3 border-[#D1D1D1] w-[266px] mt-1" @click.stop>
    <div class="flex flex-col gap-4">
      <template v-for="item in menuItems" :key="item.key || item.label">
        <!-- Heading -->
        <div v-if="item.type === 'title'"
          class="font-medium text-xs uppercase text-[#6E6E6E] bg-[#fff] focus:outline-none">
          {{ item.label || t(item.key) }}
        </div>

        <!-- Divider -->
        <hr v-else-if="item.type === 'divider'" class="my-2 border-[#D1D1D1]" />

        <!-- Expandable Item -->
        <div v-else-if="item.children">
          <button :id="item.id" @click="toggleChild(item)" :class="[
            'flex justify-between w-full text-left rounded hover:text-[#762123] cursor-pointer bg-[#fff] focus:outline-none text-sm text-[#000000]',
              item.type === 'heading' ? 'font-semibold' : 'font-semibold',
              item.expanded ? 'text-[#762123] font-semibold' : 'text-[#000000]',
          ]">
            <span>{{ item.label || t(item.key) }}</span>
            <i class="font-18" :class="item.expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
          </button>

          <div class="pt-2" v-if="item.expanded">
            <template v-for="child in item.children" :key="child.key">
              <button
                v-if="child.hasPermission && child.popup"
                :id="child.id"
                @click="handleItemClick(child)"
                class="no-underline hover:no-underline focus:outline-none w-full"
              >
                <div class="flex items-center gap-2 py-2 font-14 font-weight-normal hover:text-[#762123] bg-[#fff] focus:outline-none text-[#000000]">
                  <i class="ri-corner-down-right-line text-[#464646]"></i>
                  <span class="text-start">{{ t(child.key) }}</span>
                </div>
              </button>

              <a
                v-else-if="child.hasPermission"
                :id="child.id"
                :href="child.route"
                class="py-4 no-underline hover:no-underline"
              >
                <button
                  class="flex items-center gap-2 py-2 font-14 font-weight-normal text-[#000] hover:text-[#762123] bg-[#fff] focus:outline-none"
                >
                  <i class="ri-corner-down-right-line text-[#464646]"></i>
                  <span class="text-start">{{ t(child.key) }}</span>
                </button>
              </a>

              <div
                v-else
                class="flex items-center gap-2 py-2 text-sm cursor-not-allowed text-[#8B8B8B]"
              >
                <i class="ri-corner-down-right-line text-[#8B8B8B]"></i>
                <span class="text-start text-[#8B8B8B]">
                  {{ t(child.key) }}
                </span>
              </div>

            </template>
          </div>

        </div>

        <!-- Simple Item -->
        <a :id="item.id" :href="item.route" v-else-if="item.hasPermission" @click="handleItemClick(item)" class="text-[#464646] no-underline hover:no-underline">
          <button 
            :class="[
              'w-full text-left rounded hover:text-[#762123] cursor-pointer bg-[#fff] focus:outline-none text-sm text-[#000000]',
              item.type === 'heading' ? 'font-semibold' : 'font-normal'
            ]">
            {{ item.label || t(item.key) }}
          </button>
        </a>
        <div v-else-if="item.featureFlag" class="w-full text-left text-sm cursor-not-allowed text-[#8B8B8B]" :class="item.type === 'heading' ? 'font-semibold' : 'font-normal'">
           {{ item.label || t(item.key) }}
        </div>
      </template>
    </div>
  </div>
 
 <!-- Property Report Modal -->
 <PropertyReportModal 
   v-model="showPropertyReportModal"
   :key="propertyReportModalKey"
   :redirectToPropertyReports="true"
   @close="closePropertyReportModal"
 />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getLanguage } from '@/utilities/cookies';
import PropertyReportModal from './PropertyReportModal.vue'
import { useGlobalCatalogStore } from '@/stores/globalCatalogStore';

const globalCatalogStore = useGlobalCatalogStore();
const isOrderingViewer = computed(() => globalCatalogStore.userRoles?.includes('OrderingViewer') ?? false);

const { t, locale } = useI18n()
locale.value = getLanguage();

const emit = defineEmits(['close', 'open-popup'])
const popupRef = ref(false)
const showPropertyReportModal = ref(false)
const propertyReportModalKey = ref(0)

const isExpanded = ref([])

const menuItems = computed(() => [
  { type: 'title', key: 'globalCatalog.createNew' },  
  { key: 'globalCatalog.order', type: 'heading', route: '/new-order', id: 'global-catalog-main-order', hasPermission: isOrderingViewer.value, featureFlag: true },
  { key: 'globalCatalog.estimate',type: 'heading', route: '/estimator?from=vr', id: 'global-catalog-main-estimate', hasPermission: isOrderingViewer.value, featureFlag: true },
  { key: 'globalCatalog.orderTemplate', type: 'heading', route: '/ordertemplate?TemplateType=NewTemplate&showOptions=true', id: 'global-catalog-main-order-template', hasPermission: isOrderingViewer.value, featureFlag: true },
  { key: 'globalCatalog.quoteRequest', type: 'heading', route: '/quote-request', id: 'global-catalog-main-quote-request', hasPermission: isOrderingViewer.value, featureFlag: true },
  {
    key: 'globalCatalog.propertyReport',
    type: 'heading',
    expanded: isExpanded.value['global-catalog-toggle-property-report'] ?? false,
    id: 'global-catalog-toggle-property-report',
    hasPermission: true,
    featureFlag: true,
    children: [
      { key: 'globalCatalog.orderNewPropertyReport', popup: 'HomeOwnerPropertyReport', id: 'global-catalog-child-order-new-property-report', hasPermission: isOrderingViewer.value },
      { key: 'globalCatalog.downloadExistingReports', route: '/property-reports', id: 'global-catalog-child-download-prior-reports', hasPermission: true }
    ]
  },
  { type: 'divider' },
  { type: 'title', key: 'globalCatalog.fromIntegrations' },
  {
    label: 'EagleView',
    expanded: isExpanded.value['global-catalog-toggle-eagleview'] ?? false,
    id: 'global-catalog-toggle-eagleview',
    hasPermission: true,
    featureFlag: true,
    children: [
      { key: 'globalCatalog.orderNewReport', route: '/report-ordering', id: 'global-catalog-child-eagleview-order-new-report', hasPermission: isOrderingViewer.value },
      { key: 'globalCatalog.viewExistingReports', route: '/integrations#EagleView', id: 'global-catalog-child-eagleview-view-existing-reports', hasPermission: true }
    ]
  },
  
  {
    label: 'GAF QuickMeasure',
    expanded: isExpanded.value['global-catalog-toggle-gaf-quickmeasure'] ?? false,
    id: 'global-catalog-toggle-gaf-quickmeasure',
    hasPermission: true,
    featureFlag: true,
    children: [
      { key: 'globalCatalog.orderAReport', route: '/gaf-report-ordering', id: 'global-catalog-child-gaf-order-a-report', hasPermission: true },
      { key: 'globalCatalog.viewExistingReports', route: '/integrations#GAF', id: 'global-catalog-child-gaf-view-reports', hasPermission: true }
    ]
  },
  {
    label: 'Hover',
    expanded: isExpanded.value['global-catalog-toggle-hover'] ?? false,
    id: 'global-catalog-toggle-hover',
    hasPermission: true,
    featureFlag: true,
    children: [
      { key: 'globalCatalog.uploadBlueprint', route: '/upload-blueprint', popup: 'HoverIntegration', id: 'global-catalog-child-hover-upload-blueprint', hasPermission: true },
      { key: 'globalCatalog.inviteToCapture', route: '/invite-to-capture', popup: 'HoverIntegration', id: 'global-catalog-child-hover-invite-to-capture', hasPermission: true },
      { key: 'globalCatalog.viewExistingReports', route: '/integrations#Hover', popup: 'HoverIntegration', id: 'global-catalog-child-hover-view-reports', hasPermission: true }
    ]
  }
])



function toggleChild(item) { 
  menuItems.value.forEach(i => { if (i.children) isExpanded.value[i.id] = i === item ? !i.expanded : false }) 
}


function handleItemClick(item) {
  if (item.popup === 'QuickMeasure') {
    emit('open-popup', item.popup, item.route)
    emit('close')
    return
  }

  if (item.popup === 'HoverIntegration') {
    emit('open-popup', item.popup, item.route)
    emit('close')
    return
  } else if (item.popup === 'HomeOwnerPropertyReport') {
    // If feature flag is enabled, navigate to the dedicated page instead of opening dialog
    if (window.appConfig?.enableCreditCardPropertyReporty) {
      window.location.href = '/order-homeowner-insights'
      emit('close')
      return
    }
    propertyReportModalKey.value++
    showPropertyReportModal.value = true
    return
  }

  if (item.route) {
    window.location.href = item.route
  }

  emit('close')
}

function closePropertyReportModal() {
  showPropertyReportModal.value = false
}

function handleClickOutside(event) {
  if (showPropertyReportModal.value) {
    return
  }
  if (popupRef.value && !popupRef.value.contains(event.target)) {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
});
</script>